import {
  DeferToIdleCallback,
  DeferToIdleOptions,
  DeferToIdleType
} from "./defer-to-idle.types";

/**
 * Defers callback execution to browser idle time to avoid blocking INP (Interaction to Next Paint).
 * Uses requestIdleCallback when available, falls back to setTimeout.
 *
 * @param callback - Function to execute during idle time
 * @param options - Configuration options
 * @param options.timeout - Maximum time to wait before forcing execution (default: 2000ms)
 *
 * @example
 * ```typescript
 * deferToIdle(() => {
 *   // Heavy tracking or analytics work
 *   sendAnalytics();
 * });
 *
 * // With custom timeout
 * deferToIdle(() => {
 *   sendAnalytics();
 * }, { timeout: 5000 });
 * ```
 */
export const deferToIdle: DeferToIdleType = (
  callback: DeferToIdleCallback,
  options?: DeferToIdleOptions
) => {
  const { timeout = 2000 } = options || {};

  if (typeof window === "undefined") {
    callback();
    return;
  }

  const win = window as Window & {
    requestIdleCallback?: (
      idleCallback: IdleRequestCallback,
      idleOptions?: IdleRequestOptions
    ) => number;
  };

  if (win.requestIdleCallback) {
    win.requestIdleCallback(() => callback(), { timeout });
    return;
  }

  win.setTimeout(callback, 0);
};
